import {
  Category,
  ProductPrice,
  ComboSpec,
  Product,
} from "@tendercuts/models/catalog";
import { Injectable } from "@angular/core";
import { Subscription } from "rxjs";
import { map, skip } from "rxjs/operators";
import { AngularFirestore } from "@angular/fire/firestore";
import { AppState } from "@store/state";
import { Store } from "@ngrx/store";
import {
  ActionCatalogLoadSuccess,
  ActionCatalogLoadFailed,
  ActionLoadComboLinks,
  ActionEliteCatalogLoadSuccess,
} from "@store/catalog";
import { GenericError } from "@tendercuts/models";

@Injectable({ providedIn: "root" })
export class FirebaseCatalogService {
  catalogSubscription$: Subscription;
  priceSubscription$: Subscription;
  linksSubscription$: Subscription;
  eliteCatalogSubscription$: Subscription;

  catalog: Category[] = null;
  links: { [id: number]: ComboSpec[] } = {};
  prices: ProductPrice[] = null;
  eliteProducts: Product[] = null;

  constructor(private afs: AngularFirestore, private store: Store<AppState>) {}

  /**
   * Intitate the subscription for catalog leaf in fireabse - which fetches the
   * catalog products and convert it to Catalog model from firebase
   * and then publish it to redux
   * @param storeId for which Store the catalog
   * subscription must be initated
   */
  private startCatalogSubscription(storeId) {
    const catalogStatusDb = this.afs.doc("catalog/" + storeId.toString());
    const pipeline$ = catalogStatusDb.valueChanges().pipe(
      map((data) => (data ? data[storeId] : [])),
      map((data: Category[]) =>
        data.map((cat) => new Category().deserialize(cat))
      )
    );
    this.catalogSubscription$ = pipeline$.subscribe((categories) => {
      if (!categories.length) {
        this._publishError();
        return;
      }

      this.catalog = categories;
      this.publishCatalog();
    });
  }

  /**
   * Intitate the subscription for link leaf in fireabse - which fetches the
   * combo link products and convert it to ComboSpec model from firebase
   * and then publish it to redux
   * @param storeId for which Store the link ( combo link )
   * subscription must be initated
   */
  private startComboLinksSubscription(storeId) {
    const linksStatusDb = this.afs.doc("link/" + storeId.toString());
    const pipeline$ = linksStatusDb
      .valueChanges()
      .pipe(map((data) => (data ? data[storeId] : {})));
    this.linksSubscription$ = pipeline$.subscribe((comboLinks: any) => {
      if (!comboLinks) {
        this._publishError();
        return;
      }

      for (let key in comboLinks) {
        let spec: ComboSpec[] = [];
        comboLinks[key].forEach((item) => {
          spec.push(new ComboSpec().deserialize(item));
        });

        this.links[key] = spec;
      }
      this.publishLinks();
    });
  }

  /**
   * Intitate the subscription for price leaf in fireabse - which fetches the
   * catalog product prices and convert it to ProductPrice model from firebase
   * and then publish it to redux
   * @param storeId for which Store the catalog  product price
   * subscription must be initated
   */
  private startPriceSubscription(storeId) {
    const priceStatusDb = this.afs.doc("price/" + storeId.toString());
    const pipeline$ = priceStatusDb.valueChanges().pipe(
      map((data) => (data ? data[storeId] : [])),
      map((data: ProductPrice[]) =>
        data.map((price) => new ProductPrice().deserialize(price))
      )
    );
    this.priceSubscription$ = pipeline$.subscribe((prices) => {
      if (!prices.length) {
        this._publishError();
        return;
      }

      this.prices = prices;
      this.publishCatalog();
    });
  }

  /**
   * Populate the price for each products form the
   * price data fetched from firebase
   * and then publish the catalog with price to redux
   */
  private publishCatalog(): void {
    if (!(this.catalog && this.prices)) {
      return;
    }

    let priceMap = {};
    this.prices.forEach((price) => (priceMap[price.productId] = price));
    this.catalog.forEach((cat) => cat.populatePriceForProducts(priceMap));

    this.store.dispatch(new ActionCatalogLoadSuccess(this.catalog));
  }

  /**
   * Publich the error to redux if any subscrioption was not
   * initated sucessfully
   */
  private _publishError() {
    this.store.dispatch(
      new ActionCatalogLoadFailed(
        new GenericError({
          message: "Not able to fetch, Contact Tech Support",
        })
      )
    );
  }

  /** PUblish the combi links to the redux */
  private publishLinks() {
    if (!this.links) {
      return;
    }

    this.store.dispatch(new ActionLoadComboLinks(this.links));
  }

  /**
   * Initiate the subscription to fetch the data's from firebase
   * @param storeId - for which store the subscription must be initiate
   */
  startSubscription(storeId) {
    this.startCatalogSubscription(storeId);
    this.startPriceSubscription(storeId);
    this.startComboLinksSubscription(storeId);
    this.startEliteProductSubscription(storeId);
  }

  /**
   * Unsubscribe all the subscription made to the firebase.
   */
  unSubscription() {
    this.priceSubscription$.unsubscribe();
    this.catalogSubscription$.unsubscribe();
    this.linksSubscription$.unsubscribe();
    this.eliteCatalogSubscription$.unsubscribe();
  }

  /**
   * Intitate the subscription for elite_catalog - which fetches the
   * elite products as Product model from firebase
   * and publish it to redux
   * @param storeId for which store the elite products
   * subscription must be initated
   */
  startEliteProductSubscription(storeId: number): void {
    const eliteCatalStatusDb = this.afs.doc(
      "subscription/" + storeId
    );
    const pipeline$ = eliteCatalStatusDb.valueChanges().pipe(
      map((data) => (data ? data[storeId] : [])),
      map((data: Product[]) => {
        if (!data) return [];
        return data.map((product) => new Product().deserialize(product));
      })
    );
    this.eliteCatalogSubscription$ = pipeline$.subscribe((products) => {
      if (!products.length) {
        this._publishError();
        return;
      }
      this.eliteProducts = products;

      this.store.dispatch(
        new ActionEliteCatalogLoadSuccess(this.eliteProducts)
      );
    });
  }
}
